import React, { useEffect, useState, useCallback } from 'react';
import {
  History, Loader2, ShieldCheck, AlertTriangle, RefreshCw,
} from 'lucide-react';
import { Button } from '../index';
import { ccGetBindingTransitions } from '../../../services/api';

/**
 * P0-1 — FrameworkBindingTransitionsTimeline
 *
 * <p>Vertical timeline of every state-machine transition for a single
 * {@code FrameworkBinding}, rendered from the per-binding hash chain
 * returned by {@code GET /compliance-center/bindings/{id}/transitions}.
 * Designed for the Compliance Center binding detail surface; auditors
 * and compliance owners read it top-to-bottom to see when each binding
 * moved through PENDING → SKILL_CREATED → RULES_GENERATED → TESTING →
 * ACTIVE (or sideways into FAILED/ARCHIVED).</p>
 *
 * <h3>Why this is read-only</h3>
 * <p>Mutations to the transition log would defeat its tamper-evidence
 * purpose. Every entry is part of a SHA-256 hash chain rooted at a
 * GENESIS sentinel, so any tampering breaks downstream verification.
 * State changes happen through the existing advance / archive / fail
 * endpoints which themselves emit new transition rows.</p>
 *
 * <h3>Props</h3>
 * <ul>
 *   <li><code>bindingId</code> — required UUID.</li>
 *   <li><code>compact</code> — default false. When true, renders fewer
 *       columns (good for slideouts).</li>
 *   <li><code>onLoaded</code> — optional callback fired with the
 *       transitions array once they're fetched.</li>
 * </ul>
 */
export function FrameworkBindingTransitionsTimeline({
  bindingId,
  compact = false,
  onLoaded,
}) {
  const [state, setState] = useState('idle'); // idle | loading | loaded | error
  const [rows, setRows] = useState([]);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!bindingId) return;
    setState('loading');
    setError(null);
    try {
      const r = await ccGetBindingTransitions(bindingId);
      if (r?.ok === false) {
        setState('error');
        setError(r.error || 'Failed to load transitions');
        return;
      }
      const list = Array.isArray(r) ? r : (r?.data || r?.body || []);
      setRows(list);
      setState('loaded');
      if (typeof onLoaded === 'function') onLoaded(list);
    } catch (e) {
      setState('error');
      setError(e?.message || String(e));
    }
  }, [bindingId, onLoaded]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="bg-v2-surface border border-v2-border-subtle rounded-v2-md p-4">
      <div className="flex items-center gap-2 mb-3">
        <History size={14} className="text-v2-action-strong" />
        <h3 className="text-[13px] font-semibold text-v2-ink m-0">
          State transition history
        </h3>
        {state === 'loaded' && (
          <span className="text-[11px] text-v2-ink-muted">
            · {rows.length} entr{rows.length === 1 ? 'y' : 'ies'}
          </span>
        )}
        <Button
          variant="ghost" size="sm"
          iconLeft={state === 'loading'
            ? <Loader2 size={12} className="animate-spin" />
            : <RefreshCw size={12} />}
          onClick={load}
          disabled={state === 'loading'}
          className="ml-auto"
        >
          Reload
        </Button>
      </div>

      <p className="text-[12px] text-v2-ink-secondary m-0 mb-4">
        Each entry is a tamper-evident record of a state change. The
        per-binding hash chain links every row to its predecessor; the
        genesis row's previous hash is the literal sentinel{' '}
        <code className="font-mono-v2 text-[11px]">GENESIS</code>.
      </p>

      {state === 'loading' && (
        <div className="flex items-center gap-2 text-[12.5px] text-v2-ink-muted py-4">
          <Loader2 size={14} className="animate-spin" /> Loading transition log…
        </div>
      )}

      {state === 'error' && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-v2-sm bg-v2-warning-soft border border-v2-warning/30 text-[12px] text-v2-warning-strong">
          <AlertTriangle size={14} />
          <span>Could not load transitions: {error}</span>
        </div>
      )}

      {state === 'loaded' && rows.length === 0 && (
        <p className="text-[12.5px] text-v2-ink-muted py-2">
          No transitions recorded yet. Every framework binding should carry an
          initial creation event in its history. If this binding is missing one,
          contact your platform administrator to backfill the transition log.
        </p>
      )}

      {state === 'loaded' && rows.length > 0 && (
        <ol className="relative pl-6 space-y-3" role="list">
          {/* Vertical rail */}
          <span
            aria-hidden
            className="absolute left-[10px] top-2 bottom-2 w-px bg-v2-border-subtle"
          />
          {rows.map((row, i) => (
            <li
              key={row.id || row.transitionHash || i}
              className="relative bg-v2-surface-muted/40 rounded-v2-sm border border-v2-border-subtle p-3"
            >
              {/* Bullet */}
              <span
                aria-hidden
                className={`absolute left-[-22px] top-[14px] w-3 h-3 rounded-full border-2 ${
                  row.toStatus === 'ARCHIVED'  ? 'bg-v2-ink-muted border-v2-ink-muted'   :
                  row.toStatus === 'FAILED'    ? 'bg-v2-danger border-v2-danger'         :
                  row.toStatus === 'ACTIVE'    ? 'bg-v2-success border-v2-success'       :
                  row.toStatus === 'TESTING'   ? 'bg-v2-warning border-v2-warning'       :
                                                 'bg-v2-action border-v2-action'
                }`}
              />

              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="font-mono-v2 text-[11px] text-v2-ink-muted">
                  {row.fromStatus || <em className="not-italic text-v2-ink-secondary">∅</em>}
                </span>
                <span className="text-v2-ink-muted text-[12px]">→</span>
                <span className={`font-mono-v2 text-[11.5px] font-semibold ${
                  row.toStatus === 'FAILED'    ? 'text-v2-danger-strong'   :
                  row.toStatus === 'ARCHIVED'  ? 'text-v2-ink-muted'       :
                  row.toStatus === 'ACTIVE'    ? 'text-v2-success-strong'  :
                                                 'text-v2-action-strong'
                }`}>
                  {row.toStatus}
                </span>

                <span className="ml-auto text-[11px] text-v2-ink-muted font-mono-v2"
                      title={row.transitionedAt}>
                  {row.transitionedAt
                    ? new Date(row.transitionedAt).toLocaleString()
                    : '—'}
                </span>
              </div>

              {row.reason && (
                <p className="text-[12px] text-v2-ink mt-1 mb-0">
                  {row.reason}
                </p>
              )}

              <div className="flex items-center gap-3 mt-2 flex-wrap text-[10.5px] text-v2-ink-muted">
                <span title="Who triggered this transition">
                  Actor: <strong className="text-v2-ink">{row.actorKind || 'USER'}</strong>
                  {row.actorUserId && (
                    <span className="ml-1 font-mono-v2">
                      ({String(row.actorUserId).slice(0, 8)}…)
                    </span>
                  )}
                </span>

                {!compact && (
                  <span title="The SHA-256 hash of the prior transition in this binding's chain. Auditors re-compute and compare.">
                    <ShieldCheck size={11} className="inline mr-1" />
                    prev:{' '}
                    <code className="font-mono-v2">
                      {row.previousTransitionHash === 'GENESIS'
                        ? 'GENESIS'
                        : `${String(row.previousTransitionHash || '').slice(0, 8)}…`}
                    </code>
                  </span>
                )}

                {!compact && row.transitionHash && (
                  <span title="The SHA-256 hash of THIS transition. The next transition in the chain references this value.">
                    hash:{' '}
                    <code className="font-mono-v2">
                      {String(row.transitionHash).slice(0, 8)}…
                    </code>
                  </span>
                )}

                {row.coverageSnapshot
                  && row.coverageSnapshot.coveragePercent != null && (
                  <span title="Coverage at the moment of this transition. Historical evidence — not affected by later recalculations.">
                    Coverage:{' '}
                    <strong className="text-v2-ink">
                      {Number(row.coverageSnapshot.coveragePercent).toFixed(1)}%
                    </strong>
                  </span>
                )}
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

export default FrameworkBindingTransitionsTimeline;
